/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ChatMessageBody from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageBody';
import ChatMessageFooter from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageFooter';
import ChatMessageHeader from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageHeader';
import ChatRecommendations from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/ChatRecommendations';
import type { MessageTypes } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  isCoachMessage as isCoachMessageUtil,
  isErrorMessage as isErrorMessageUtil,
  isTextMessage as isTextMessageUtil,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';

export type Props = React.PropsWithChildren<{
  /**
   * current chat message
   */
  message: MessageTypes;
  /**
   * zero-based index of the current message
   */
  index: number;
  /**
   * all chat messages in store currently
   */
  messages: MessageTypes[];
  sendMessage: SendMessageFunc;
  learnerPersona: CoachPersona;
  coachPersona: CoachPersona;

  hideRecommendedActions?: boolean;
  searchTerm?: string;
  recommendedActionsLabel?: string;
  onRecommendedActionClick?: () => void;
}>;

const styles = {
  footer: css`
    justify-content: end;
  `,
};

const ChatMessage = ({
  message,
  index,
  messages,
  sendMessage,
  coachPersona,
  learnerPersona,
  children,
  hideRecommendedActions,
  searchTerm,
  recommendedActionsLabel,
  onRecommendedActionClick,
}: Props) => {
  const { sender } = message;

  const isLastMessage = index === messages.length - 1;
  const isCoachMessage = isCoachMessageUtil(sender);
  const persona = isCoachMessage ? coachPersona : learnerPersona;

  // only allow Coach text messages to be copied
  const showCopyMessage = isCoachMessage && isTextMessageUtil(message as MessageTypes);
  const isErrorMessage = isCoachMessage && isErrorMessageUtil(message as MessageTypes);

  return (
    <>
      <ChatMessageHeader senderName={persona.name} senderAvatar={persona.avatar} />

      <ChatMessageBody message={message} className="coach-chat-message-body" searchTerm={searchTerm}>
        {/* hide recommended actions in search mode to prevent sending new messages in this mode */}
        {isCoachMessage && !hideRecommendedActions ? (
          <ChatRecommendations
            message={message}
            sendMessage={sendMessage}
            showRecommendedActions={isLastMessage}
            recommendedActionsLabel={recommendedActionsLabel}
            onRecommendedActionClick={onRecommendedActionClick}
          />
        ) : null}
        {children}
      </ChatMessageBody>

      {/* hide message actions for Coach error messages */}
      {isCoachMessage && !isErrorMessage && (
        <ChatMessageFooter showCopyMessage={showCopyMessage} css={styles.footer} message={message} />
      )}
    </>
  );
};

export default ChatMessage;
